import { createSlice } from "@reduxjs/toolkit";
import { fetch as fetchUsers } from "services/user-select.service";
import { fetch } from "../../services/customers.service";

const CustomersSlice = createSlice({
  name: "customers",
  initialState: {
    customersCollection: [],
    // A fetch is dispatched on mount, so default to loading to avoid an
    // empty-table flash before the first request starts.
    isLoading: true,
    // Whether the first response (success or error) has arrived. Lets the UI
    // tell "still loading" apart from "loaded, but no customers".
    hasLoaded: false,
    isError: false,
    errMsg: null,
    queryValue: null,
    users: {},
    filtersValue: {},
    showAll: false,
    // Whether `customersCollection` came from an id-restricted fetch ("show
    // only selected"). The page must not present such a list as the full one
    // while the unrestricted refetch is still on its way.
    isRestrictedToIds: false,
  },
  /* eslint-disable no-param-reassign */
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setCustomers(state, action) {
      state.isLoading = false;
      state.hasLoaded = true;
      state.customersCollection = action.payload.customersCollection;
      state.isRestrictedToIds = !!action.payload.isRestrictedToIds;
    },
    setUsers(state, action) {
      for (let i = 0; i < action.payload.usersCollection.length; i++) {
        const { id, name } = action.payload.usersCollection[i];
        state.users[id] = name;
      }
    },
    setError(state, action) {
      state.isLoading = false;
      state.hasLoaded = true;
      state.isError = true;
      state.errMsg = action.payload?.errMsg;
      // A failed refetch must not leave a selection-only list standing in for
      // the full one.
      if (state.isRestrictedToIds) {
        state.customersCollection = [];
        state.isRestrictedToIds = false;
      }
    },
    setFilterValue(state, action) {
      const { key, value } = action.payload;
      state.filtersValue[key] = value;
    },
    setShowAll(state, action) {
      state.showAll = action.payload;
    },
  },
  /* eslint-enable */
});

// Extract the action creators object and the reducer
const { actions, reducer } = CustomersSlice;

// Extract and export each action creator by name
export const {
  setLoading,
  setCustomers,
  setUsers,
  setError,
  setFilterValue,
  setShowAll,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;

// Only the newest list request may write the store: toggling "show only
// selected" or typing a search fires requests that can resolve out of order.
let latestFetchId = 0;

export const fetchCustomers =
  (companyId, filtersValue, showAll = false, ids) =>
  async (dispatch) => {
    const fetchId = ++latestFetchId;
    const isRestrictedToIds = Array.isArray(ids) && ids.length > 0;
    dispatch(setLoading());
    try {
      const { customersCollection } = await fetch(
        companyId,
        filtersValue,
        showAll,
        ids
      );
      if (fetchId !== latestFetchId) return;
      dispatch(setCustomers({ customersCollection, isRestrictedToIds }));
    } catch (e) {
      if (fetchId !== latestFetchId) return;
      dispatch(setError({ errMsg: "" }));
      return;
    }

    // Users only label the "assigned user" filter chips. Kept out of the list
    // request's try: a failure here must not flag — or, for an id-restricted
    // list, wipe — customers that have already loaded.
    try {
      const { usersCollection } = await fetchUsers(companyId);
      dispatch(setUsers({ usersCollection }));
    } catch (e) {
      // The chips fall back to showing the raw value.
    }
  };
